import {
  createAPIHooks,
  APIClient,
  type APIQueryHooks,
} from '@lifeomic/one-query';
import type { RestAPIEndpoints } from '@lifeomic/react-client/rest-api-types';
import type { Endpoints as MemberOperationsServiceMembersEndpoints } from '@lifeomic/service-clients/member-operations-service-members';
import type { RegenerativeMedicineRestEndpoints } from '../types/regenerativeMedicineRestEndpoints';
import useServiceAuth from './useServiceAuth';

/**
 * Routes/shapes not yet available from the installed schema packages.
 *
 * - The regenerative-medicine routes add `lifecycleStates` / `centerLocations`
 *   filters not yet in service-clients' generated schema.
 * - The member-operations `employees/me` route is not in any generated schema
 *   yet.
 * - Composite-report routes shipped in @lifeomic/react-client v9.22.1 (react-client #222).
 *   They are now available directly via RestAPIEndpoints and no longer need
 *   local stubs here.
 * - Zori-analytics routes shipped in @lifeomic/react-client v9.24.0
 *   (react-client #226 / service-clients #270). They are now available directly
 *   via RestAPIEndpoints and no longer need local stubs here.
 * - Zori Pulse guidance read routes shipped in @lifeomic/service-clients
 *   v4.28.0 (service-clients #274 / FOTN-1650). react-client re-exports the
 *   agents endpoints via the bare `@lifeomic/service-clients/agents-api-service`
 *   import, so bumping service-clients alone surfaces them on RestAPIEndpoints;
 *   the model types below are derived from that generated response rather than
 *   hand-written.
 * - Zori Pulse auto-scan eligibility shipped in service-clients #283. Bumping
 *   `@lifeomic/service-clients` surfaces
 *   `GET /v1/agents-v2/zori-pulse/subjects/:subject_id/auto-scan-eligibility`
 *   on RestAPIEndpoints without a local stub.
 * - The zori-audit rule-actions filters, the Document Extraction guidance read
 *   route, and the Zori Pulse action-plan feedback route were reconciled with
 *   the deployed service in service-clients #296, so all three now come from
 *   the generated schema instead of local stubs.
 * - `GET /v1/agents-v2/models` accepts `surface=clinician`, which adds the
 *   latest models behind the `latest-model` toggle. Not yet in the generated
 *   schema.
 */

/** A stored guidance version with lifecycle + audit metadata. */
export type ZoriPulseGuidanceConfig =
  RestAPIEndpoints['GET /v1/agents-v2/zori-pulse/guidance']['Response'][number];

/** Versioned guidance content for a single Pulse scan category. */
export type ZoriPulseGuidanceBundle = ZoriPulseGuidanceConfig['guidance'];

/** A single learned guidance statement with feedback provenance. */
export type ZoriPulseGuidanceRule =
  ZoriPulseGuidanceBundle['avoid_rules'][number];

/** Lifecycle of a stored guidance bundle: draft -> active -> superseded. */
export type ZoriPulseGuidanceStatus = ZoriPulseGuidanceConfig['status'];

/** One learned guidance category per Pulse scan child. */
export type ZoriPulseScanCategory = ZoriPulseGuidanceBundle['scan_category'];

/**
 * A stored Document Extraction guidance version with the reflective learning
 * lifecycle (`draft`/`superseded` states and approval metadata on top of the
 * code-first config states). Derived from the guidance read route rather than
 * `@lifeomic/react-client`'s hand-written `DocumentExtractionV2GuidanceConfig`,
 * which still models only the code-first states.
 */
export type DocumentExtractionV2GuidanceReviewConfig =
  RestAPIEndpoints['GET /v1/agents-v2/document-extraction-v2/guidance']['Response'][number];

/** Versioned Document Extraction guidance content (field + observation rules). */
export type DocumentExtractionV2GuidanceReviewBundle =
  DocumentExtractionV2GuidanceReviewConfig['guidance'];

/** Lifecycle of a stored DEX guidance bundle: draft -> active -> superseded. */
export type DocumentExtractionV2GuidanceReviewStatus =
  DocumentExtractionV2GuidanceReviewConfig['status'];

export type {
  GenomicsReportJob,
  GenomicsReportJobStatus,
} from '@lifeomic/react-client';

type PhcRestEndpointExtensions = RegenerativeMedicineRestEndpoints &
  MemberOperationsServiceMembersEndpoints & {
    'GET /v1/agents-v2/models': Omit<
      RestAPIEndpoints['GET /v1/agents-v2/models'],
      'Request'
    > & {
      Request: { surface?: 'clinician' };
    };
    'GET /v1/member-operations/employees/me': {
      Request: Record<string, never>;
      Response: {
        employee: {
          id: string;
          email: string;
          phcUsername?: string;
          fhirId?: string;
          firstName?: string;
          lastName?: string;
          role?: string;
        };
      };
    };
    'POST /v1/agents-v2/survey-fill/invoke': {
      Request: {
        subject_id: string;
        project_id: string;
        response_id: string;
      };
      Response: { task_id: string };
    };
    'GET /v1/agents-v2/survey-fill/invocations': {
      Request: { subject_id: string; response_id?: string };
      Response: Array<{
        id: string;
        status: string;
        created?: number;
        agentInput?: { response_id?: string };
        agentOutput?: {
          filledCount?: number;
          filled_count?: number;
          fhirFilledCount?: number;
          fhir_filled_count?: number;
          zoriFilledCount?: number;
          zori_filled_count?: number;
          failureReason?: string;
          failure_reason?: string;
        };
      }>;
    };
  };

/**
 * ai-template-service declares `maxItems: 50` on composite-report `sections`,
 * and the client codegen expands that bound into a union of 51 tuple types
 * (`[] | [S] | [S, S] | ...`) instead of `S[]`. A plain array is not assignable
 * to that union, so every caller that writes a composite report fails to
 * type-check. The bound is a server-side validation concern rather than a shape
 * the client needs to model, so restate the two write routes with `sections` as
 * an ordinary array. Remove this once the schema stops emitting `maxItems` (or
 * the codegen stops expanding it) — see ai-template-service.
 */
type AiTemplateWriteRoute =
  | 'POST /v1/ai-templates'
  | 'PATCH /v1/ai-templates/:id';

type CompositeReportSection = NonNullable<
  Extract<
    RestAPIEndpoints['POST /v1/ai-templates']['Request'],
    { type: 'compositeReportDefinition' }
  >['sections']
>[number];

/**
 * Rewrites `sections` in place across each member of the request union. This
 * has to stay a homomorphic mapped type rather than `Omit<T, 'sections'> & {…}`:
 * the intersection form defeats the discriminated-union narrowing that callers
 * rely on when they pass a value whose `type` is still the full union, and it
 * would drop the `?` modifier that makes `sections` optional on PATCH.
 */
type WithArraySections<T> = T extends unknown
  ? { [K in keyof T]: K extends 'sections' ? CompositeReportSection[] : T[K] }
  : never;

type AiTemplateWriteOverrides = {
  [Route in AiTemplateWriteRoute]: Omit<RestAPIEndpoints[Route], 'Request'> & {
    Request: WithArraySections<RestAPIEndpoints[Route]['Request']>;
  };
};

export type PhcPlatformRestEndpoints = Omit<
  RestAPIEndpoints,
  AiTemplateWriteRoute
> &
  AiTemplateWriteOverrides &
  PhcRestEndpointExtensions;
import axios from 'axios';
import { useMemo } from 'react';
import {
  withE2ERequestStubbing,
  withAccountHeader,
  withAuthHeader,
  withConfiguredBaseUrl,
  withRetries,
  compose,
} from './useService';
import withPathPrefix from './useService/util/withPathPrefix';
import { useActiveAccountId } from './useActiveAccountId';
import { QueryClient } from '@tanstack/react-query';
import { isInternalQueryKey } from '@lifeomic/one-query/util';

const useOneQueryClient = () => {
  const auth = useServiceAuth();
  // `useServiceAuth` returns a ref whose `.account` is only synced inside a
  // `useEffect`, so reading `auth.account` here lags one render behind an
  // account switch. The axios instance is memoized on that stale value, so a
  // REST call fired right after switching accounts can still carry the previous
  // account in its `LifeOmic-Account` header (cross-account data leakage). Read
  // the active account from the live selector so the client is rebuilt with the
  // correct account header as soon as the account changes.
  const account = useActiveAccountId();

  const client = useMemo(() => {
    const axiosInstance = axios.create();
    const composed = compose(
      withE2ERequestStubbing,
      withAccountHeader(account),
      withAuthHeader(auth.authToken),
      withConfiguredBaseUrl(auth.config),
      withPathPrefix('/api'),
      withRetries(),
    )(axiosInstance);

    // Serialize arrays as repeated keys (`_tag=a&_tag=b`) so FHIR search
    // params match platform expectations.
    composed.defaults.paramsSerializer = { indexes: null };

    return composed;
  }, [auth.authToken, account, auth.config]);

  return client;
};

/**
 * Axios client for PHC `/api` REST calls (auth, base URL, retries).
 * Use when a route needs non-default request shaping (e.g. POST with query params).
 */
export const usePhcRestAxios = useOneQueryClient;

export const REST_API_HOOKS_NAME = 'phc-ui';

const phcRestApiHooks = createAPIHooks<PhcPlatformRestEndpoints>({
  name: REST_API_HOOKS_NAME,
  client: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useOneQueryClient();
  },
});

/**
 * `react-query`-style hooks for the platform REST API.
 */
// Prefer `createAPIHooks<RestAPIEndpoints>`: react-client's emitted `createRestAPIHooks` return type omits newer routes.
export const apiHooks: APIQueryHooks<PhcPlatformRestEndpoints> =
  phcRestApiHooks;

/**
 * Returns an `axios`-style client for the platform REST API.
 * Memoized so consumers can safely list this in `useEffect` dependency arrays
 * without re-running effects every render (the underlying axios instance already
 * updates when auth/config changes).
 */
export const useApiClient = (): APIClient<PhcPlatformRestEndpoints> => {
  const client = useOneQueryClient();
  return useMemo(
    () => new APIClient<PhcPlatformRestEndpoints>(client),
    [client],
  );
};

/**
 * Updates the cached data for queries matching a specific route and predicate in the given query client.
 *
 * @param queryClient - The QueryClient instance whose queries' data will be updated.
 * @param route - The API route string to match against each query's internal query key.
 * @param predicate - A function that receives the query payload and returns true if the query should be updated.
 * @param dataTransformer - A function that receives the current cached data and returns the new data to be set.
 * @returns The result of the dataTransformer for each updated query.
 */
export const setQueriesData = <PayloadT, DataT>(
  queryClient: QueryClient,
  {
    route,
    predicate,
    dataTransformer,
  }: {
    route: string;
    predicate: (payload: PayloadT) => boolean;
    dataTransformer: (oldData: DataT | undefined) => DataT | undefined;
  },
) => {
  queryClient.setQueriesData(
    {
      predicate: query => {
        const internalQueryKey = query.queryKey.find(isInternalQueryKey);
        if (!internalQueryKey) {
          return false;
        }
        if (internalQueryKey.route !== route) {
          return false;
        }
        const payload = internalQueryKey.payload as PayloadT;
        return predicate(payload);
      },
    },
    dataTransformer,
  );
};
